import * as Icons from "lucide-react";
import { Target, Eye, HeartHandshake, Award, Cpu, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/sections/CTABanner";
import { IMAGES } from "@/data/site";

const VALUES = [
  { icon: "Target", title: "Our Mission", desc: "To deliver accurate, timely and patient-focused diagnostic care that empowers better healthcare decisions for every patient we serve." },
  { icon: "Eye", title: "Our Vision", desc: "To be recognised as the most trusted and technologically advanced diagnostic center in Lucknow and beyond." },
  { icon: "HeartHandshake", title: "Patient Care Commitment", desc: "Every patient is treated with empathy, dignity and comfort — from the moment they walk in to report delivery." },
];

const STANDARDS = [
  { icon: "Award", title: "Quality Standards", desc: "Rigorous quality-control protocols and double-checked reporting ensure dependable, accurate results." },
  { icon: "Cpu", title: "Modern Equipment", desc: "State-of-the-art ultrasound, digital X-ray and laboratory systems, regularly calibrated and maintained." },
  { icon: "Users", title: "Experienced Team", desc: "Senior radiologists, pathologists and trained technicians dedicated to diagnostic precision." },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <PageHeader
        eyebrow="About Us"
        title="Trusted Diagnostic Excellence in Lucknow"
        subtitle="UNIRAD DIAGNOSTICS combines advanced imaging technology with experienced medical professionals to deliver diagnostics you can rely on."
      />

      {/* Overview */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-border shadow-xl">
              <img src={IMAGES.reception} alt="UNIRAD Diagnostics modern facility reception" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="label-eyebrow">Company Overview</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-medium tracking-tight text-brand-navy text-balance">
              Diagnostics built on accuracy and trust
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              UNIRAD DIAGNOSTICS provides reliable diagnostic imaging and pathology services using modern technology and expert medical professionals. Located in the heart of Adarsh Nagar, Lucknow, we are committed to making high-quality diagnostics accessible, affordable and comfortable for every patient.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              From advanced ultrasound and digital X-ray to a comprehensive pathology lab, our integrated diagnostic services help patients and doctors make confident, informed healthcare decisions — with most reports delivered the very same day.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { n: "15+", l: "Years of Care" },
                { n: "50k+", l: "Tests Performed" },
                { n: "4.9", l: "Patient Rating" },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl border border-border bg-brand-mist p-4 text-center">
                  <p className="font-heading text-2xl font-bold text-brand">{s.n}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission / Vision / Care */}
      <section className="py-20 sm:py-24 bg-brand-mist">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="label-eyebrow">What Drives Us</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-medium tracking-tight text-brand-navy text-balance">
              Purpose-led, patient-first
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => {
              const Icon = Icons[v.icon];
              return (
                <Reveal key={v.title} delay={i * 0.1}>
                  <div className="h-full rounded-2xl bg-white border border-border p-7">
                    <span className="grid place-items-center h-14 w-14 rounded-2xl bg-brand text-white">
                      <Icon className="h-6 w-6" strokeWidth={1.6} />
                    </span>
                    <h3 className="mt-5 font-heading text-xl font-semibold text-brand-navy">{v.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team image band */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <p className="label-eyebrow">Quality & Expertise</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-medium tracking-tight text-brand-navy text-balance">
              Standards that earn your trust
            </h2>
            <div className="mt-8 space-y-6">
              {STANDARDS.map((s) => {
                const Icon = Icons[s.icon];
                return (
                  <div key={s.title} className="flex gap-4">
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-soft text-brand shrink-0">
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-brand-navy text-lg">{s.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-1">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="rounded-3xl overflow-hidden border border-border shadow-xl">
              <img src={IMAGES.team} alt="Experienced medical team at UNIRAD Diagnostics" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </main>
  );
}
